/**
 * Line Item Calculation Utility
 *
 * This utility calculates individual product line prices with discounts.
 * Used in quote product line creation to ensure consistency with quote totals.
 */

import { IPriceBreakdownList } from "@/features/assistant/types";


interface CalculateLineItemParams {
  itemPrice: IPriceBreakdownList;
  quantity: number;
  customDiscountPercentage: number;
  customDiscountAmount?: number;
  discountType?: 'percentage' | 'amount';
  subtotal?: number; // Required when discountType is 'amount' to calculate proportional discount
  /** Customer VAT % */
  vatPercentage: number;
}

interface LineItemCalculation {
  // Per unit values
  itemCustomDiscount: number;
  itemTotalDiscountPerUnit: number;
  priceAfterDiscountPerUnit: number;
  itemTotalPrice: number;

  // Total values (for all quantities)
  extendedAmount: number;
  taxAmount: number;
  totalDiscountAmount: number;
  lineItemTotal: number;
}

/**
 * Round a number to 2 decimal places (standard for currency)
 */
const roundToTwoDecimals = (value: number): number => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Calculate line item totals with VSS and custom discounts
 *
 * @param params - Item price, quantity, and discount percentages/amounts
 * @returns Calculated line item values including discounts and tax
 */
export const calculateLineItem = ({
  itemPrice,
  quantity,
  customDiscountPercentage,
  customDiscountAmount = 0,
  discountType = 'percentage',
  subtotal = 0,
  vatPercentage,
}: CalculateLineItemParams): LineItemCalculation => {
  const itemTotalPrice = itemPrice.totalPrice;

  // Calculate custom discount for this item (per unit)
  let itemCustomDiscount: number;

  if (discountType === 'amount' && customDiscountAmount > 0 && subtotal > 0) {
    // Calculate proportional discount based on this item's share of the subtotal
    const itemExtendedPrice = itemTotalPrice * quantity;
    const itemProportion = itemExtendedPrice / subtotal;
    const itemTotalDiscount = customDiscountAmount * itemProportion;
    itemCustomDiscount = roundToTwoDecimals(itemTotalDiscount / quantity);
  } else {
    // Use percentage discount
    itemCustomDiscount = roundToTwoDecimals(
      itemTotalPrice * (customDiscountPercentage / 100),
    );
  }

  // Total discount per unit (rounded)
  const itemTotalDiscountPerUnit = roundToTwoDecimals(itemCustomDiscount);

  // Price after discount per unit (rounded)
  const priceAfterDiscountPerUnit = roundToTwoDecimals(
    itemTotalPrice - itemTotalDiscountPerUnit,
  );

  // Extended amount (total for all quantities before tax) - rounded
  const extendedAmount = roundToTwoDecimals(
    priceAfterDiscountPerUnit * quantity,
  );

  // Tax on discounted price (for all quantities) using customer vatPercentage - rounded
  const taxAmount = roundToTwoDecimals(extendedAmount * (vatPercentage / 100));

  // Total discount for all quantities (rounded)
  const totalDiscountAmount = roundToTwoDecimals(itemTotalDiscountPerUnit * quantity);

  // Line item total (extended amount + tax) - rounded
  const lineItemTotal = roundToTwoDecimals(extendedAmount + taxAmount);

  return {
    itemCustomDiscount,
    itemTotalDiscountPerUnit,
    priceAfterDiscountPerUnit,
    itemTotalPrice,
    extendedAmount,
    taxAmount,
    totalDiscountAmount,
    lineItemTotal,
  };
};
